import { memo, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Typography,
} from '@mui/material';
import { CloseIcon } from '~/assets/icons';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { BaseSelect } from '~/components/common';
import { handleGetAreaScopeList } from '~/thunks/areaScope/areaScopeThunk';
import { useParams } from 'react-router-dom';
import { IAreaScopeList } from '~/utils/interface/areaScope';

type AddRoomProps = {
  open: boolean;
  setOpen: (open: boolean, needRefreshData?: boolean) => void;
  addAffectedField: (roomId: string, roomName: string) => void; // Updated prop
  currentScreen: 'areaScope' | 'psychrometicReading';
  type: 'affected' | 'dh';
  roomArea?: string;
};

function ModalAddRoomDialog(props: AddRoomProps) {
  const dispatch = useAppDispatch();
  const { id, areaId } = useParams();
  const isAreaScopeScreen = props.currentScreen === 'areaScope';
  const isCreateAreaScopeScreen = isAreaScopeScreen && areaId === 'create';
  const isEditAreaScopeScreen = isAreaScopeScreen && !isNaN(Number(areaId));
  const { t } = useTranslation(['admin/job']);
  const [roomId, setRoomId] = useState<string>(isEditAreaScopeScreen ? String(areaId) : '');
  const [roomName, setRoomName] = useState<string>(''); // Capture room name
  const [errorText, setErrorText] = useState<string>('');
  const { areaScopes } = useAppSelector((state) => state.areaScope);
  const [areaScopeOptions, setAreaScopeOptions] = useState<Array<IAreaScopeList>>(areaScopes);
  useEffect(() => {
    if (id) {
      dispatch(handleGetAreaScopeList({ jobId: id }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Only create room area
    if (isCreateAreaScopeScreen) {
      if (props.roomArea && props.roomArea !== '') {
        const areaScope: IAreaScopeList = {
          id: '',
          name: props.roomArea ?? '',
          jobId: Number(id),
          detail: {},
        };

        setAreaScopeOptions([areaScope, ...areaScopes]);
      } else {
        setAreaScopeOptions(areaScopes);
      }
    }
  }, [areaId, areaScopes, id, isCreateAreaScopeScreen, props.roomArea]);

  useEffect(() => {
    const selectedOption = areaScopeOptions.find((scope) => scope.id === roomId);
    setRoomName(selectedOption?.name || ''); // Store room name
  }, [areaScopeOptions, roomId]);

  const handleSetOpen = (open: boolean) => {
    setRoomId(isEditAreaScopeScreen ? String(areaId) : '');
    setErrorText('');
    props.setOpen && props.setOpen(open);
  };

  const handleSubmit = () => {
    if (roomId || (isCreateAreaScopeScreen && roomId === '')) {
      props.addAffectedField(roomId, roomName);
      handleSetOpen(false); // Close the modal
    } else {
      setErrorText(t('estimateTemplate.required'));
    }
  };
  const modalTitle = props.type === 'affected' ? t('addAffected') : t('addDh');
  const handleCancel = () => {
    handleSetOpen(false);
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {modalTitle}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='p-0'>
        <Box className='mt-3 w-full'>
          <InputLabel className='content-center'>Room</InputLabel>
          <BaseSelect
            name='templateId'
            className='w-full'
            placeholder='Please select a room'
            value={roomId}
            options={areaScopeOptions}
            required={true}
            onChange={(event) => {
              setRoomId(event.target.value);
            }}
          ></BaseSelect>
          <Typography className='text-red-600 text-[12px]'>{errorText}</Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={handleSubmit}>
          {t('insert', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(ModalAddRoomDialog);
